import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';

// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

const LOCAL_ACTOR_KEY = 'developer-actor-id';
const LOCAL_DEVELOPER_UNLOCK_KEY = 'developer-unlocked';
const LOCAL_RATE_PER_COMMIT_KEY = 'developer-rate-per-commit';
const LOCAL_RATE_PER_HOUR_KEY = 'developer-rate-per-hour';
const LOCAL_REPO_OWNER_KEY = 'developer-github-owner';
const LOCAL_REPO_NAME_KEY = 'developer-github-name';

const DEV_PREFS_COLLECTION = 'developerPrefs';
const DEV_PREFS_DOC_ID = 'shared';

const shouldDisableFirestore = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';

const getDocRef = async () => {
  const db = await getDb();
  if (!db) throw new Error('Firestore is not configured.');
  return doc(db, DEV_PREFS_COLLECTION, DEV_PREFS_DOC_ID);
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] developerPrefs', detail);
  } catch {
    // ignore
  }
};

// Keep in sync with Developer defaults.
const DEFAULT_RATE_PER_COMMIT = 25;
const DEFAULT_RATE_PER_HOUR = 65;

export const generateId = () => {
  // crypto.randomUUID is not available on some Safari/iOS versions.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }

  // Best-effort RFC4122 v4 using getRandomValues.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const toHex = (n: number) => n.toString(16).padStart(2, '0');
      const hex = Array.from(bytes, toHex).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    // ignore
  }

  // Final fallback (not cryptographically strong, but prevents runtime crashes).
  const rand = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${rand()}${rand()}-${rand()}-${rand()}-${rand()}-${rand()}${rand()}${rand()}`;
};

export const getOrCreateActorId = () => {
  if (typeof window === 'undefined') return 'server';
  const stored = window.localStorage.getItem(LOCAL_ACTOR_KEY);
  if (stored) return stored;
  const next = generateId();
  window.localStorage.setItem(LOCAL_ACTOR_KEY, next);
  // Fire and forget - save to Firestore without blocking
  Promise.resolve().then(async () => {
    const db = await getDb();
    if (db && !shouldDisableFirestore()) {
      try {
        logFirestoreWrite({ action: 'saveActorId' });
        void setDoc(await getDocRef(), { actorId: next }, { merge: true });
      } catch {
        // ignore network errors
      }
    }
  });
  return next;
};

export const getStoredRatePerCommit = () => {
  if (typeof window === 'undefined') return DEFAULT_RATE_PER_COMMIT;
  const stored = window.localStorage.getItem(LOCAL_RATE_PER_COMMIT_KEY);
  if (!stored) return DEFAULT_RATE_PER_COMMIT;
  const parsed = Number(stored);
  return Number.isNaN(parsed) || parsed <= 0 ? DEFAULT_RATE_PER_COMMIT : parsed;
};

export const setStoredRatePerCommit = (rate: number) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_RATE_PER_COMMIT_KEY, String(rate));
  // Fire and forget - save to Firestore without blocking
  Promise.resolve().then(async () => {
    const db = await getDb();
    if (db && !shouldDisableFirestore()) {
      try {
        logFirestoreWrite({ action: 'saveRatePerCommit', rate });
        void setDoc(await getDocRef(), { ratePerCommit: rate }, { merge: true });
      } catch {
        // ignore write errors
      }
    }
  });
};

export const getStoredRatePerHour = () => {
  if (typeof window === 'undefined') return DEFAULT_RATE_PER_HOUR;
  const stored = window.localStorage.getItem(LOCAL_RATE_PER_HOUR_KEY);
  if (!stored) return DEFAULT_RATE_PER_HOUR;
  const parsed = Number(stored);
  return Number.isNaN(parsed) || parsed <= 0 ? DEFAULT_RATE_PER_HOUR : parsed;
};

export const setStoredRatePerHour = (rate: number) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_RATE_PER_HOUR_KEY, String(rate));
  // Fire and forget - save to Firestore without blocking
  Promise.resolve().then(async () => {
    const db = await getDb();
    if (db && !shouldDisableFirestore()) {
      try {
        logFirestoreWrite({ action: 'saveRatePerHour', rate });
        void setDoc(await getDocRef(), { ratePerHour: rate }, { merge: true });
      } catch {
        // ignore write errors
      }
    }
  });
};

export const getStoredDeveloperUnlock = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(LOCAL_DEVELOPER_UNLOCK_KEY) === 'true';
  } catch {
    return false;
  }
};

export const setStoredDeveloperUnlock = (value: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LOCAL_DEVELOPER_UNLOCK_KEY, value ? 'true' : 'false');
    window.dispatchEvent(new Event('developer-unlocked-changed'));
  } catch {
    // ignore storage errors (e.g., privacy mode)
  }
};

export const getStoredRepoOwner = () => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(LOCAL_REPO_OWNER_KEY) ?? '';
};

export const setStoredRepoOwner = (owner: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_REPO_OWNER_KEY, owner);
  // Fire and forget - save to Firestore without blocking
  Promise.resolve().then(async () => {
    const db = await getDb();
    if (db && !shouldDisableFirestore()) {
      try {
        logFirestoreWrite({ action: 'saveRepoOwner', owner });
        void setDoc(await getDocRef(), { repoOwner: owner }, { merge: true });
      } catch {
        // ignore write errors
      }
    }
  });
};

export const getStoredRepoName = () => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(LOCAL_REPO_NAME_KEY) ?? '';
};

export const setStoredRepoName = (repo: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_REPO_NAME_KEY, repo);
  // Fire and forget - save to Firestore without blocking
  Promise.resolve().then(async () => {
    const db = await getDb();
    if (db && !shouldDisableFirestore()) {
      try {
        logFirestoreWrite({ action: 'saveRepoName', repo });
        void setDoc(await getDocRef(), { repoName: repo }, { merge: true });
      } catch {
        // ignore write errors
      }
    }
  });
};

const hydrateFromRemote = async () => {
  const db = await getDb();
  if (!db || shouldDisableFirestore()) return;
  const ref = await getDocRef();
  try {
    const snapshot = await getDocFromServer(ref);
    if (!snapshot.exists()) return;
    const data = snapshot.data() as Partial<Record<string, unknown>>;
    if (typeof data.actorId === 'string') window.localStorage.setItem(LOCAL_ACTOR_KEY, data.actorId);
    if (typeof data.ratePerCommit === 'number') window.localStorage.setItem(LOCAL_RATE_PER_COMMIT_KEY, String(data.ratePerCommit));
    if (typeof data.ratePerHour === 'number') window.localStorage.setItem(LOCAL_RATE_PER_HOUR_KEY, String(data.ratePerHour));
    if (typeof data.repoOwner === 'string') window.localStorage.setItem(LOCAL_REPO_OWNER_KEY, data.repoOwner);
    if (typeof data.repoName === 'string') window.localStorage.setItem(LOCAL_REPO_NAME_KEY, data.repoName);
  } catch {
    try {
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) return;
      const data = snapshot.data() as Partial<Record<string, unknown>>;
      if (typeof data.actorId === 'string') window.localStorage.setItem(LOCAL_ACTOR_KEY, data.actorId);
      if (typeof data.ratePerCommit === 'number') window.localStorage.setItem(LOCAL_RATE_PER_COMMIT_KEY, String(data.ratePerCommit));
      if (typeof data.ratePerHour === 'number') window.localStorage.setItem(LOCAL_RATE_PER_HOUR_KEY, String(data.ratePerHour));
      if (typeof data.repoOwner === 'string') window.localStorage.setItem(LOCAL_REPO_OWNER_KEY, data.repoOwner);
      if (typeof data.repoName === 'string') window.localStorage.setItem(LOCAL_REPO_NAME_KEY, data.repoName);
    } catch {
      // ignore fetch errors
    }
  }
};

// Export for on-demand hydration from components
export const hydrateStorageFromRemote = hydrateFromRemote;
